<script setup lang="ts">
import type { HeadingType, UIProp } from '~/types/elementsUi'
import type { FAIconName } from '#shared/types/generated-icons'

type VCardEmits = {
  iconClick: []
}

interface VCardProps {
  title?: string
  titleElementType?: HeadingType

  icon?: FAIconName
  fullHeight?: boolean

  clickableIcon?: boolean
  noBorder?: boolean
  noBoxShadow?: boolean

  ui?: UIProp<
    | 'base'
    | 'header'
    | 'headerTitle'
    | 'title'
    | 'headerIcon'
    | 'container'
    | 'content'
    | 'footer'
  >
}

const emits = defineEmits<VCardEmits>()
const props = withDefaults(defineProps<VCardProps>(), {
  titleElementType: 'h3',
  title: undefined,
  icon: undefined,
  ui: undefined
})

const { t, te } = useI18n()

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, {
    title: tw`mb-0! text-xl!`
  })
)
</script>

<template>
  <div
    class="card"
    :class="[
      fullHeight && 'h-full',
      noBorder && 'no-border',
      noBoxShadow && 'no-box-shadow',
      ui?.base
    ]"
  >
    <slot name="image" />

    <div class="card-container" :class="ui?.container">
      <slot name="header">
        <header class="card-header" :class="ui?.header">
          <div
            v-if="$slots['title'] || title"
            class="card-header-title"
            :class="ui?.headerTitle"
          >
            <slot name="title">
              <div class="title-subtitle">
                <component :is="titleElementType" :class="computedUi?.title">
                  {{ title && te(title) ? t(title) : title }}
                </component>
              </div>
            </slot>
          </div>

          <VHtmlButton
            v-if="$slots['icon'] || icon"
            class="card-header-icon"
            :class="[ui?.headerIcon, clickableIcon && 'cursor-pointer']"
            @click="emits('iconClick')"
          >
            <slot name="icon">
              <VIcon v-if="icon" :icon size="large" :force-clickable="clickableIcon" />
            </slot>
          </VHtmlButton>
        </header>
      </slot>

      <div class="card-content" :class="ui?.content">
        <slot name="content" />
      </div>

      <footer v-if="$slots['footer']" class="card-footer" :class="ui?.footer">
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<style scoped></style>
